import { Component, inject, TemplateRef, ViewChild } from '@angular/core';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { TimesheetStaffErrorcodeComponent } from '../timesheet-staff-errorcode/timesheet-staff-errorcode.component';
import { ListTableComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { CommonModule } from '@angular/common';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { TimesheetFilterComponent } from "../timesheet-filter/timesheet-filter.component";
import { ListService } from '../../../../shared/services/list.service';
import { LookupService } from '../../../../services/lookup.service';
import { AccordionComponent } from '@ppl/ui';
import { UserService } from '../../../../services/user.service';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';

@Component({
  selector: 'ppl-timesheet-provider-denied-list',
  standalone: true,
  imports: [ListTableComponent, NgZorroModule, CommonModule,
    TimesheetFilterComponent, AccordionComponent, TimesheetStaffErrorcodeComponent],
  templateUrl: './timesheet-provider-denied-list.component.html',
  styleUrl: './timesheet-provider-denied-list.component.css',
})
export class TimesheetProviderDeniedListComponent {
  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;
  @ViewChild('deniedDialog') deniedDialog!: TemplateRef<any>;
  @ViewChild('participantRejectedDialog') participantRejectedDialog!: TemplateRef<any>;
  private dialogSvc = inject(DialogService);

  deniedData: any[] = [];
  participantRemarks: string | null = null;
  actionButtons: TableRowActionsModel = new TableRowActions({ view: true });
  appliedFilters: any;
  providerId: any;
  programName!: string;
  processingStatusList: LookupValueItem[] = [];
  timesheets: any[] = [];

  filterFields = [
    { field: 'Timeframe', show: true, default: '30 days' },
    { field: 'processStatusLkp', show: true },
    { field: 'consumer', show: true },
  ];

  cols = [
    {
      field: 'serviceDate',
      header: 'Service Date',
      jsonpath: 'serviceDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'consumerName',
      header: 'Participant Name',
      jsonpath: 'consumerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'participantPplId',
      header: 'Participant PPL ID',
      jsonpath: 'participantPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'timeIn',
      header: 'Time In',
      jsonpath: 'timeIn',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'timeOut',
      header: 'Time Out',
      jsonpath: 'timeOut',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'billedUnitsInHrs',
      header: 'Billed Hours',
      jsonpath: 'billedUnitsInHrs',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'processingStatus',
      header: 'Processing Status',
      jsonpath: 'processingStatus',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
      // Every row is Denied or Participant Denied (the API is scoped to D/CD), so the whole column links.
      isClickable: true,
    },
  ];

  constructor(private listService: ListService, private lookupService: LookupService, private userService: UserService,
    private dateTimeFormatterService: DateTimeFormatterService) { }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.providerId = JSON.parse(this.userService.getSelectedLead()!);
    this.programName = selectedProgram?.program_code ?? '';
    this.processingStatusList = this.lookupService.getLookupValue('TS_App_S');
    this.onSearch({});
  }

  buildRequestPayload(pageName: string, searchParams: any[], startingPageNumber = 1, recordsPerPage = 20) {
    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      recordsPerPage: recordsPerPage,
      startingPageNumber: startingPageNumber,
      orderByColumn: ['service_date'],
      orderBy: ['asc'],
      searchParam: searchParams,
    };
    return payload;
  }

  searchTimesheets(filters: any, startingPageNumber = 1, currentPage = 0, recordsPerPage = 20) {
    const payload = this.buildRequestPayload(
      'denied-consumerdenied-timesheets',
      filters,
      startingPageNumber,
      recordsPerPage
    );
    this.listService
      .getListData(payload, 'claims')
      .subscribe((data: any) => {
        const existing = startingPageNumber > 1 ? this.timesheets : [];
        const records = this.groupSplitEntries(this.processData(data.responsedata ?? []), existing);
        this.timesheets = [...existing, ...records];
        if (this.childComponent) {
          this.childComponent.recordsPerPage = recordsPerPage;
          this.childComponent.resetPagination(startingPageNumber, currentPage);
        }
      });
  }

  processData(data: any[]) {
    data.forEach((element: any) => {
      element.consumerName = element.consumer_name;
      element.participantPplId = element.participant_ppl_id;
      element.serviceDate = element.service_date;
      element.timeIn = element.time_in;
      element.timeOut = element.time_out;
      element.billedUnitsInHrs = element.billed_units === 0 || element.billed_units ? this.dateTimeFormatterService.formatUnitsToHoursMinutes(element.billed_units) : null;

      const processingStatus = this.processingStatusList.find((status: LookupValueItem) => status.lkpValueCode === element.prcocessing_status_lkp);
      element.processingStatus = processingStatus ? processingStatus.lkpValueName : element.prcocessing_status_lkp;
    });

    return data;
  }

  /**
   * Nest split entries under their SPLT transaction: rows whose parent_tas_timesheetline_detail_id
   * matches a SPLT row's tas_timesheetline_detail_id become that row's lineItems (shown expanded,
   * no extra API call) and are removed from the top level. Parents already loaded on earlier pages
   * are matched too.
   */
  groupSplitEntries(records: any[], existing: any[]) {
    const splitParents = new Map<any, any>();
    [...existing, ...records]
      .filter((row: any) => row.business_status_lkp === 'SPLT')
      .forEach((row: any) => {
        row.lineItems = row.lineItems ?? [];
        row.showLineItem = row.showLineItem ?? true;
        splitParents.set(row.tas_timesheetline_detail_id, row);
      });

    return records.filter((row: any) => {
      const parent = splitParents.get(row.parent_tas_timesheetline_detail_id);
      if (row.parent_tas_timesheetline_detail_id == null || !parent || parent === row) {
        return true;
      }
      parent.lineItems.push(row);
      return false;
    });
  }

  hasSplitEntries(row: any): boolean {
    return row?.business_status_lkp === 'SPLT' && row.lineItems?.length > 0;
  }

  onRowExpanded(row: any) {
    // Split entries are already loaded with the page, so expanding/collapsing only toggles visibility.
    if (this.hasSplitEntries(row)) {
      row.showLineItem = !row.showLineItem;
    }
  }

  // ppl-list-table renders the expand icon on every row; mark flat rows so the CSS hides it.
  cellClassResolver = (row: any) => (this.hasSplitEntries(row) ? null : 'no-split-entries');

  onPageLoad(event: any) {
    this.searchTimesheets(this.appliedFilters, event.startingPageNumber, event?.first ?? 0, event.rows ?? 20);
  }

  onSearch(event: any) {
    const filters = [];
    const timeframe = event.Timeframe ? event.Timeframe : '30 days';
    filters.push({
      jsonPath: "serviceDuration",
      value: timeframe.replace(/\D/g, ''),
      operator: "lessThan"
    });

    filters.push({
      jsonPath: "providerId",
      value: this.providerId,
      operator: "equal"
    });

    if (event.consumer && event.consumer.trim()) {
      filters.push({
        jsonPath: "consumer_name",
        value: event.consumer.trim(),
        operator: "contains"
      });
    }

    if (event.processStatusLkp) {
      filters.push({
        jsonPath: "prcocessing_status_lkp",
        values: [event.processStatusLkp, 'MBRK', 'OLAP', 'AUTZ'],
        operator: "in"
      });
    } else {
      filters.push({
        jsonPath: "prcocessing_status_lkp",
        values: ['D', 'CD', 'MBRK', 'OLAP', 'AUTZ'],
        operator: "in"
      });
    }

    this.appliedFilters = filters;
    this.searchTimesheets(filters, undefined, undefined, this.childComponent?.recordsPerPage ?? 20);
  }

  onCellClicked(event: any) {
    if (event.col?.field !== 'processingStatus') {
      return;
    }

    const row = event.rowData;
    if (row.prcocessing_status_lkp === 'CD' || row.business_status_lkp === 'CD') {
      this.participantRemarks = row.remarks;
      this.openDeniedDialog(`${row.processingStatus} Timesheet`, this.participantRejectedDialog);
      return;
    }

    const payload = {
      pageName: 'timesheet-processCode',
      programCode: this.programName,
      condition: 'and',
      recordsPerPage: 20,
      startingPageNumber: 1,
      searchParam: [
        {
          jsonPath: 'timesheetId',
          value: row.tas_timesheetline_detail_id,
          operator: 'Equal',
        },
      ],
      orderByColumn: ['id'],
      orderBy: ['desc'],
    };

    this.listService
      .getListData(payload, 'claims')
      .subscribe((data: any) => {
        this.deniedData = data.responsedata ?? [];
        this.openDeniedDialog(`${row.processingStatus} Timesheet`, this.deniedDialog);
      });
  }

  openDeniedDialog(title: string, template: TemplateRef<any>) {
    const dialogConfig: DialogConfig = {
      title: title,
      okText: 'Ok',
      size: 'x-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.dialogSvc.openDialog(template, dialogConfig, () => { }, () => { });
  }

  onAction(event: any) {
    if (event.action === 'view') {
      // TODO: wire up the view dialog once the field mapping for servicePeriodId
      // (required by ppl-timesheet-detail-view) is confirmed against this API.
    }
  }
}
